import type { MaybeRefOrGetter, Ref } from 'vue';
import type { FlattenedItem, NestedSelectMenuItem, NestedSelectMenuItemBase } from './types';
import { computed, nextTick, toValue } from 'vue';
import { hasChildren } from './utils';

export type HighlightedNestedSelectMenuValue<
	TItem extends NestedSelectMenuItemBase,
> = TItem | FlattenedItem<TItem>;

export interface UseNestedSelectMenuKeyboardOptions<
	TItem extends NestedSelectMenuItemBase = NestedSelectMenuItem,
> {
	isRtl: MaybeRefOrGetter<boolean>;
	highlightedItem: Ref<
		HighlightedNestedSelectMenuValue<TItem> | null
	>;
	expandedItemId: Ref<string | null>;
	nestedMenuRef: Ref<{ focus: () => void } | null>;
	onEscape: () => void;
	onCollapse?: () => void;
	onSelectHighlighted?: (
		item: HighlightedNestedSelectMenuValue<TItem>,
	) => void;
}

function isFlattenedItem<
	TItem extends NestedSelectMenuItemBase,
>(
	item: HighlightedNestedSelectMenuValue<TItem>,
): item is FlattenedItem<TItem> {
	return 'item' in item && 'pathIds' in item;
}

function getMenuItem<
	TItem extends NestedSelectMenuItemBase,
>(
	item: HighlightedNestedSelectMenuValue<TItem>,
): TItem {
	return isFlattenedItem(item) ? item.item : item;
}

export function useNestedSelectMenuKeyboard<
	TItem extends NestedSelectMenuItemBase = NestedSelectMenuItem,
>(
	options: UseNestedSelectMenuKeyboardOptions<TItem>,
) {
	const expandKey = computed(() => toValue(options.isRtl) ? 'ArrowLeft' : 'ArrowRight');
	const collapseKey = computed(() => toValue(options.isRtl) ? 'ArrowRight' : 'ArrowLeft');

	async function focusNestedMenuAfterExpansion() {
		await nextTick();
		options.nestedMenuRef.value?.focus();
	}

	function expandHighlightedItem(item: TItem) {
		options.expandedItemId.value = item.id;
		void focusNestedMenuAfterExpansion();
	}

	function handleKeydown(event: KeyboardEvent) {
		const highlightedItem = options.highlightedItem.value;
		const highlightedMenuItem = highlightedItem ? getMenuItem(highlightedItem) : null;

		if (event.key === expandKey.value && highlightedMenuItem && hasChildren(highlightedMenuItem)) {
			event.preventDefault();
			expandHighlightedItem(highlightedMenuItem);
			return;
		}

		if (event.key === collapseKey.value && options.onCollapse) {
			event.preventDefault();
			options.onCollapse();
			return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			options.onEscape();
			return;
		}

		if (event.key === 'Enter') {
			if (!highlightedItem || !highlightedMenuItem) return;

			if (isFlattenedItem(highlightedItem)) {
				event.preventDefault();
				options.onSelectHighlighted?.(highlightedItem);
				return;
			}

			if (hasChildren(highlightedMenuItem) && !highlightedMenuItem.selectable) {
				event.preventDefault();
				expandHighlightedItem(highlightedMenuItem);
				return;
			}

			event.preventDefault();

			if (hasChildren(highlightedMenuItem) && highlightedMenuItem.selectable) {
				options.expandedItemId.value = highlightedMenuItem.id;
			}

			options.onSelectHighlighted?.(highlightedItem);
		}
	}

	return { handleKeydown, expandKey, collapseKey };
}
